import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';

import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import type { ParsedDateRange } from '@/lib/parseDateRange';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { AffiliateData } from '@/services/payment/payment.types';
import { getRowYear } from '../utils/monthDateRange';
import { formatMonthAndYear, getColumns } from './Column';

export function AffiliateLogTable({
  data = [],
  pagination,
  loading,
  dateRange,
}: {
  data?: AffiliateData[];
  pagination: Pagination;
  loading: boolean;
  dateRange: ParsedDateRange;
}) {
  const { t } = useDynamicLocale();
  const columns = React.useMemo(() => getColumns(t, dateRange), [t, dateRange]);

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const exportColumnById: Record<string, { header: string; value: (row: AffiliateData, index: number) => unknown }> = {
    sn: { header: t('common.page.tableColumn.sn'), value: (_row, index) => getSerialNumber(table, index) },
    monthAndYear: {
      header: t('common.page.tableColumn.monthAndYear'),
      value: (row) => formatMonthAndYear(row.monthNumber, getRowYear(row, dateRange)),
    },
    createdDate: {
      header: t('common.page.tableColumn.withdrawDay'),
      value: (row) => row.withdrawDate,
    },
    totalRequestedAmount: {
      header: t('common.page.tableColumn.totalRequestedAmount'),
      value: (row) => formatAmount(row.totalRequestedAmount),
    },
  };

  const handleExport = () => {
    const exportColumns = table
      .getVisibleLeafColumns()
      .flatMap(({ id }) => (exportColumnById[id] ? [exportColumnById[id]] : []));
    const exportRows = table
      .getFilteredRowModel()
      .rows.map(({ original, index }) =>
        Object.fromEntries(exportColumns.map(({ header, value }) => [header, value(original, index)])),
      );

    exportCsvSingleFilePapa(exportRows, {
      fileName: 'affiliate-withdrawal-logs-report',
      format: 'YYYY/MM/DD HH:mm:ss',
      headers: exportColumns.map(({ header }) => header),
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    meta: {
      defaultDatePreset: 'today',
      hideSearch: true,
      title: <Translation id="paymentManagement.affiliatePaymentLogs.table.title" defaultMessage="Withdrawal Logs" />,
      totalPage: pagination.totalItems,
      loading,
      actionKey: 'exportCsv',
      action: handleExport,
    },
    getRowId: (originalRow: AffiliateData) => originalRow.paymentId,
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <DataTable table={table}>
      <DataTableToolbar dateFilterFirst={true} defaultDatePreset="today" allowFutureDates hideViewOptions />
    </DataTable>
  );
}
